<template>
  <v-col cols="12" class="catalogo-col">
    <div class="catalogo-page">

      <!-- HERO -->
      <header class="catalogo-hero">
        <div class="catalogo-hero__main">
          <div class="catalogo-hero__icon">
            <v-icon size="28">mdi-bookshelf</v-icon>
          </div>
          <div class="catalogo-hero__text">
            <h1 class="catalogo-hero__title">Catalogos</h1>
            <p class="catalogo-hero__subtitle">
              Administra todos los elementos maestros utilizados por las maquinas y herramientas del sistema.
            </p>
          </div>
        </div>

        <div class="catalogo-hero__stats">
          <div class="catalogo-hero__stat">
            <span class="catalogo-hero__stat-value">{{ totalRegistros }}</span>
            <span class="catalogo-hero__stat-label">Registros totales</span>
          </div>
          <div class="catalogo-hero__stat-divider"></div>
          <div class="catalogo-hero__stat">
            <span class="catalogo-hero__stat-value">{{ catalogosVisibles }}</span>
            <span class="catalogo-hero__stat-label">Catalogos activos</span>
          </div>
        </div>
      </header>

      <!-- CARD GRID -->
      <section class="catalogo-grid">
        <article
          v-for="section in visibleSections"
          :key="'card-' + section.key"
          ref="card"
          class="catalogo-card"
          :class="[
            'catalogo-card--' + section.color,
            { 'catalogo-card--active': section.key === activeKey }
          ]"
          @click="activateSection(section.key)"
          @keyup.enter="activateSection(section.key)"
          tabindex="0"
        >
          <div class="catalogo-card__top">
            <div class="catalogo-card__icon">
              <v-icon size="26">{{ section.icon }}</v-icon>
            </div>
            <span v-if="section.key === activeKey" class="catalogo-card__pill">
              <v-icon size="12">mdi-check</v-icon>
              Activo
            </span>
          </div>

          <h3 class="catalogo-card__title">{{ section.label }}</h3>
          <p class="catalogo-card__description">{{ section.description }}</p>

          <div class="catalogo-card__footer">
            <span class="catalogo-card__count">
              <span v-if="!section.loading" class="catalogo-card__count-num">
                {{ section.count }}
              </span>
              <span v-else class="catalogo-card__count-skeleton"></span>
              <span class="catalogo-card__count-label">
                {{ section.label.toLowerCase() }} registrados
              </span>
            </span>
            <span class="catalogo-card__cta">
              {{ section.key === activeKey ? "Ver activo" : "Ver " + section.label.toLowerCase() }}
              <v-icon small>mdi-arrow-right</v-icon>
            </span>
          </div>

          <div class="catalogo-card__glow" aria-hidden="true"></div>
        </article>
      </section>

      <!-- SECTION CONTENT -->
      <section class="catalogo-section" :class="'catalogo-section--' + activeSection.color">
        <header class="catalogo-section__header">
          <div class="catalogo-section__title-block">
            <div class="catalogo-section__icon">
              <v-icon>{{ activeSection.icon }}</v-icon>
            </div>
            <div>
              <h2 class="catalogo-section__title">{{ activeSection.label }}</h2>
              <p class="catalogo-section__subtitle">
                {{ activeSection.description }}
              </p>
            </div>
          </div>

          <button
            v-if="activeSection.canCreate"
            class="catalogo-section__cta"
            @click="triggerCreate"
          >
            <v-icon left small dark>mdi-plus</v-icon>
            {{ activeSection.ctaLabel }}
          </button>
        </header>

        <div class="catalogo-section__body">
          <transition name="catalogo-fade" mode="out-in">
            <div
              v-if="activeKey === 'sensores'"
              key="sensores"
              class="catalogo-section__pane"
            >
              <TiposSensores
                ref="sensores"
                :embedded="true"
                @count-changed="onCountChanged('sensores', $event)"
                @loading-changed="onLoadingChanged('sensores', $event)"
              />
            </div>
            <div
              v-else-if="activeKey === 'variables'"
              key="variables"
              class="catalogo-section__pane"
            >
              <Variables
                ref="variables"
                :embedded="true"
                @count-changed="onCountChanged('variables', $event)"
                @loading-changed="onLoadingChanged('variables', $event)"
              />
            </div>
            <div
              v-else-if="activeKey === 'tokens'"
              key="tokens"
              class="catalogo-section__pane"
            >
              <Tokens
                ref="tokens"
                :embedded="true"
                @count-changed="onCountChanged('tokens', $event)"
                @loading-changed="onLoadingChanged('tokens', $event)"
              />
            </div>
            <div
              v-else
              key="unidades"
              class="catalogo-section__pane"
            >
              <Unidad
                ref="unidades"
                :embedded="true"
                @count-changed="onCountChanged('unidades', $event)"
                @loading-changed="onLoadingChanged('unidades', $event)"
              />
            </div>
          </transition>
        </div>
      </section>
    </div>
  </v-col>
</template>

<script>
import TiposSensores from "./TiposSensores.vue";
import Variables from "./Variables.vue";
import Tokens from "./Tokens.vue";
import Unidad from "../Configuracion/Unidad.vue";

export default {
  name: "Catalogo",
  components: { TiposSensores, Variables, Tokens, Unidad },
  data() {
    return {
      activeKey: "sensores",
      counts: { sensores: 0, variables: 0, tokens: 0, unidades: 0 },
      loadings: {
        sensores: true,
        variables: true,
        tokens: true,
        unidades: true,
      },
    };
  },
  computed: {
    sections() {
      return [
        {
          key: "variables",
          label: "Variables",
          icon: "mdi-chart-line-variant",
          color: "blue",
          permKey: "sensores",
          description:
            "Variables medibles, su unidad y la nomenclatura con la que reportan los sensores.",
          ctaLabel: "Nueva Variable",
          canCreate: this.permisoVista("sensores", "c"),
          canRead: this.permisoVista("sensores", "r"),
          count: this.counts.variables,
          loading: this.loadings.variables,
        },
        {
          key: "sensores",
          label: "Sensores",
          icon: "mdi-access-point",
          color: "green",
          permKey: "sensores",
          description:
            "Dispositivos fisicos que capturan los datos de las variables.",
          ctaLabel: "Nuevo Sensor",
          canCreate: this.permisoVista("sensores", "c"),
          canRead: this.permisoVista("sensores", "r"),
          count: this.counts.sensores,
          loading: this.loadings.sensores,
        },
        {
          key: "tokens",
          label: "Tokens",
          icon: "mdi-key-variant",
          color: "purple",
          permKey: "sensores",
          description:
            "Tokens de comunicacion utilizados por las maquinas para identificarse.",
          ctaLabel: "Nuevo Token",
          canCreate: this.permisoVista("sensores", "c"),
          canRead: this.permisoVista("sensores", "r"),
          count: this.counts.tokens,
          loading: this.loadings.tokens,
        },
        {
          key: "unidades",
          label: "Unidades",
          icon: "mdi-scale-balance",
          color: "orange",
          permKey: "almacen",
          description:
            "Unidades de medida disponibles para variables y articulos del sistema.",
          ctaLabel: "Nueva Unidad",
          canCreate: this.permisoVista("almacen", "c"),
          canRead: this.permisoVista("almacen", "r"),
          count: this.counts.unidades,
          loading: this.loadings.unidades,
        },
      ];
    },
    visibleSections() {
      return this.sections.filter((s) => s.canRead);
    },
    activeSection() {
      return (
        this.visibleSections.find((s) => s.key === this.activeKey) ||
        this.visibleSections[0] ||
        this.sections[0]
      );
    },
    totalRegistros() {
      return this.visibleSections.reduce(
        (acc, s) => acc + (s.loading ? 0 : Number(s.count) || 0),
        0
      );
    },
    catalogosVisibles() {
      return this.visibleSections.length;
    },
  },
  mounted() {
    if (this.visibleSections.length && !this.visibleSections.find(s => s.key === this.activeKey)) {
      this.activeKey = this.visibleSections[0].key;
    }
  },
  methods: {
    initialize() {
      this.activateSection(this.activeKey, { force: true });
    },
    activateSection(key, options = {}) {
      const changing = this.activeKey !== key;
      this.activeKey = key;
      this.$nextTick(() => {
        if (changing || options.force) this.refreshActive();
      });
    },
    refreshActive() {
      const ref = this.$refs[this.activeKey];
      if (ref && typeof ref.initialize === "function") {
        ref.initialize();
      }
    },
    triggerCreate() {
      const ref = this.$refs[this.activeKey];
      if (ref && typeof ref.openCreateExternal === "function") {
        ref.openCreateExternal();
      }
    },
    onCountChanged(key, count) {
      this.$set(this.counts, key, count);
    },
    onLoadingChanged(key, loading) {
      this.$set(this.loadings, key, loading);
    },
  },
};
</script>

<style scoped>
/* ============================================================
   PAGE SHELL
   ============================================================ */
.catalogo-col {
  padding: 16px 18px;
}

.catalogo-page {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ============================================================
   HERO
   ============================================================ */
.catalogo-hero {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 22px 26px;
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(0, 188, 212, 0.03) 50%, rgba(142, 36, 170, 0.04) 100%),
    #ffffff;
  border: 1px solid #e6ebf2;
  box-shadow: 0 1px 2px rgba(15, 39, 78, 0.04);
  overflow: hidden;
}

.catalogo-hero::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #1976d2 0%, #00897b 33%, #8e24aa 66%, #ef6c00 100%);
}

.catalogo-hero__main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.catalogo-hero__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 12px -4px rgba(25, 118, 210, 0.45);
}

.catalogo-hero__icon >>> .v-icon {
  color: #fff !important;
}

.catalogo-hero__text {
  flex: 1;
  min-width: 0;
}

.catalogo-hero__title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.1;
}

.catalogo-hero__subtitle {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13.5px;
  line-height: 1.5;
  max-width: 640px;
}

.catalogo-hero__stats {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 4px 4px 4px 18px;
  border-left: 1px solid #eef1f6;
  flex-shrink: 0;
}

.catalogo-hero__stat {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.catalogo-hero__stat-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1;
}

.catalogo-hero__stat-label {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}

.catalogo-hero__stat-divider {
  width: 1px;
  height: 32px;
  background: #eef1f6;
}

/* ============================================================
   CARD GRID
   ============================================================ */
.catalogo-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.catalogo-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.22s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.22s ease;
  overflow: hidden;
  outline: none;
}

.catalogo-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 32px -16px rgba(15, 39, 78, 0.22),
              0 4px 8px -4px rgba(15, 39, 78, 0.08);
}

.catalogo-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.25);
}

.catalogo-card__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.catalogo-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: transform 0.25s ease;
}

.catalogo-card:hover .catalogo-card__icon {
  transform: scale(1.05) rotate(-3deg);
}

.catalogo-card__pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 999px;
}

.catalogo-card__pill >>> .v-icon {
  font-size: 12px !important;
}

.catalogo-card__title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.catalogo-card__description {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #64748b;
  flex: 1;
}

.catalogo-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #eef1f6;
  gap: 8px;
}

.catalogo-card__count {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
}

.catalogo-card__count-num {
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1;
}

.catalogo-card__count-label {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.catalogo-card__count-skeleton {
  display: inline-block;
  width: 26px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  animation: catalogo-shimmer 1.4s ease-in-out infinite;
}

@keyframes catalogo-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.catalogo-card__cta {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  transition: gap 0.2s ease;
}

.catalogo-card:hover .catalogo-card__cta {
  gap: 6px;
}

.catalogo-card__cta >>> .v-icon {
  font-size: 14px !important;
  transition: transform 0.2s ease;
}

.catalogo-card__glow {
  position: absolute;
  inset: -50% -50% auto auto;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  opacity: 0;
  filter: blur(40px);
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.catalogo-card:hover .catalogo-card__glow {
  opacity: 0.5;
}

.catalogo-card--active .catalogo-card__glow {
  opacity: 0.7;
}

/* ---------- Card Color Variants ---------- */

/* BLUE - Variables */
.catalogo-card--blue .catalogo-card__icon {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1976d2;
}
.catalogo-card--blue .catalogo-card__icon >>> .v-icon { color: #1976d2 !important; }
.catalogo-card--blue .catalogo-card__pill {
  background: rgba(25, 118, 210, 0.10);
  color: #1565c0;
}
.catalogo-card--blue .catalogo-card__pill >>> .v-icon { color: #1565c0 !important; }
.catalogo-card--blue .catalogo-card__cta { color: #1976d2; }
.catalogo-card--blue .catalogo-card__cta >>> .v-icon { color: #1976d2 !important; }
.catalogo-card--blue .catalogo-card__glow { background: #1976d2; }
.catalogo-card--blue.catalogo-card--active {
  border-color: #1976d2;
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.04) 0%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.18),
              0 12px 28px -16px rgba(25, 118, 210, 0.32);
}

/* GREEN - Sensores */
.catalogo-card--green .catalogo-card__icon {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}
.catalogo-card--green .catalogo-card__icon >>> .v-icon { color: #2e7d32 !important; }
.catalogo-card--green .catalogo-card__pill {
  background: rgba(46, 125, 50, 0.10);
  color: #2e7d32;
}
.catalogo-card--green .catalogo-card__pill >>> .v-icon { color: #2e7d32 !important; }
.catalogo-card--green .catalogo-card__cta { color: #2e7d32; }
.catalogo-card--green .catalogo-card__cta >>> .v-icon { color: #2e7d32 !important; }
.catalogo-card--green .catalogo-card__glow { background: #43a047; }
.catalogo-card--green.catalogo-card--active {
  border-color: #43a047;
  background: linear-gradient(180deg, rgba(67, 160, 71, 0.05) 0%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(67, 160, 71, 0.20),
              0 12px 28px -16px rgba(67, 160, 71, 0.32);
}

/* PURPLE - Tokens */
.catalogo-card--purple .catalogo-card__icon {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  color: #7b1fa2;
}
.catalogo-card--purple .catalogo-card__icon >>> .v-icon { color: #7b1fa2 !important; }
.catalogo-card--purple .catalogo-card__pill {
  background: rgba(123, 31, 162, 0.10);
  color: #7b1fa2;
}
.catalogo-card--purple .catalogo-card__pill >>> .v-icon { color: #7b1fa2 !important; }
.catalogo-card--purple .catalogo-card__cta { color: #7b1fa2; }
.catalogo-card--purple .catalogo-card__cta >>> .v-icon { color: #7b1fa2 !important; }
.catalogo-card--purple .catalogo-card__glow { background: #8e24aa; }
.catalogo-card--purple.catalogo-card--active {
  border-color: #8e24aa;
  background: linear-gradient(180deg, rgba(142, 36, 170, 0.04) 0%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(142, 36, 170, 0.18),
              0 12px 28px -16px rgba(142, 36, 170, 0.32);
}

/* ORANGE - Unidades */
.catalogo-card--orange .catalogo-card__icon {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  color: #e65100;
}
.catalogo-card--orange .catalogo-card__icon >>> .v-icon { color: #e65100 !important; }
.catalogo-card--orange .catalogo-card__pill {
  background: rgba(230, 81, 0, 0.10);
  color: #e65100;
}
.catalogo-card--orange .catalogo-card__pill >>> .v-icon { color: #e65100 !important; }
.catalogo-card--orange .catalogo-card__cta { color: #e65100; }
.catalogo-card--orange .catalogo-card__cta >>> .v-icon { color: #e65100 !important; }
.catalogo-card--orange .catalogo-card__glow { background: #ef6c00; }
.catalogo-card--orange.catalogo-card--active {
  border-color: #ef6c00;
  background: linear-gradient(180deg, rgba(239, 108, 0, 0.04) 0%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(239, 108, 0, 0.20),
              0 12px 28px -16px rgba(239, 108, 0, 0.32);
}

/* ============================================================
   SECTION CONTENT
   ============================================================ */
.catalogo-section {
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid #e6ebf2;
  box-shadow: 0 1px 3px rgba(15, 39, 78, 0.04);
  overflow: hidden;
}

.catalogo-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid #eef1f6;
  position: relative;
}

.catalogo-section__header::before {
  content: "";
  position: absolute;
  left: 0;
  top: 18px;
  bottom: 18px;
  width: 3px;
  border-radius: 0 2px 2px 0;
  transition: background 0.25s ease;
}

.catalogo-section--blue .catalogo-section__header::before { background: #1976d2; }
.catalogo-section--green .catalogo-section__header::before { background: #43a047; }
.catalogo-section--purple .catalogo-section__header::before { background: #8e24aa; }
.catalogo-section--orange .catalogo-section__header::before { background: #ef6c00; }

.catalogo-section__title-block {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.catalogo-section__icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.25s ease, color 0.25s ease;
}

.catalogo-section--blue .catalogo-section__icon {
  background: rgba(25, 118, 210, 0.10); color: #1976d2;
}
.catalogo-section--green .catalogo-section__icon {
  background: rgba(67, 160, 71, 0.12); color: #2e7d32;
}
.catalogo-section--purple .catalogo-section__icon {
  background: rgba(142, 36, 170, 0.10); color: #7b1fa2;
}
.catalogo-section--orange .catalogo-section__icon {
  background: rgba(239, 108, 0, 0.12); color: #e65100;
}

.catalogo-section__icon >>> .v-icon {
  color: currentColor !important;
  font-size: 20px !important;
}

.catalogo-section__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.catalogo-section__subtitle {
  margin: 2px 0 0;
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.4;
}

.catalogo-section__cta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  white-space: nowrap;
}

.catalogo-section__cta:active { transform: translateY(1px); }

.catalogo-section--blue .catalogo-section__cta {
  background: #1976d2;
  box-shadow: 0 2px 6px -2px rgba(25, 118, 210, 0.5);
}
.catalogo-section--blue .catalogo-section__cta:hover {
  background: #0d62b8;
  box-shadow: 0 6px 14px -4px rgba(25, 118, 210, 0.55);
}
.catalogo-section--green .catalogo-section__cta {
  background: #2e7d32;
  box-shadow: 0 2px 6px -2px rgba(46, 125, 50, 0.45);
}
.catalogo-section--green .catalogo-section__cta:hover {
  background: #1b5e20;
  box-shadow: 0 6px 14px -4px rgba(46, 125, 50, 0.5);
}
.catalogo-section--purple .catalogo-section__cta {
  background: #7b1fa2;
  box-shadow: 0 2px 6px -2px rgba(123, 31, 162, 0.45);
}
.catalogo-section--purple .catalogo-section__cta:hover {
  background: #6a1b9a;
  box-shadow: 0 6px 14px -4px rgba(123, 31, 162, 0.5);
}
.catalogo-section--orange .catalogo-section__cta {
  background: #ef6c00;
  box-shadow: 0 2px 6px -2px rgba(239, 108, 0, 0.45);
}
.catalogo-section--orange .catalogo-section__cta:hover {
  background: #e65100;
  box-shadow: 0 6px 14px -4px rgba(239, 108, 0, 0.5);
}

.catalogo-section__cta >>> .v-icon { color: #fff !important; }

.catalogo-section__body {
  padding: 6px 16px 14px;
  background: #fcfdff;
}

.catalogo-section__pane {
  min-height: 220px;
}

.catalogo-fade-enter-active,
.catalogo-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.catalogo-fade-enter,
.catalogo-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

/* ============================================================
   EMBEDDED CHILDREN — ROW HOVER + TABLE POLISH
   ============================================================ */
.catalogo-section__body >>> .v-data-table tbody tr {
  transition: background 0.15s ease;
}
.catalogo-section__body >>> .v-data-table tbody tr:hover {
  background: #f7fafc !important;
}
.catalogo-section__body >>> .v-data-table th {
  font-weight: 600 !important;
  font-size: 11px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #64748b !important;
}

/* ============================================================
   RESPONSIVE
   ============================================================ */
@media (max-width: 1200px) {
  .catalogo-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 768px) {
  .catalogo-col { padding: 10px 12px; }
  .catalogo-hero {
    flex-direction: column;
    align-items: stretch;
    padding: 18px 18px;
  }
  .catalogo-hero__stats {
    padding-left: 0;
    border-left: none;
    border-top: 1px solid #eef1f6;
    padding-top: 12px;
    justify-content: space-around;
  }
  .catalogo-hero__stat {
    align-items: flex-start;
  }
  .catalogo-grid {
    grid-template-columns: 1fr;
  }
  .catalogo-section__header {
    flex-direction: column;
    align-items: stretch;
  }
  .catalogo-section__cta { justify-content: center; }
}
</style>
